import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatErr } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [kicked, setKicked] = useState(false);

  useEffect(() => {
    if (params.get("kicked") === "1") {
      setKicked(true);
      // clear the flag so the interceptor can fire again next time
      try { delete window.__zfd_session_revoked__; } catch (e) { console.warn("Failed to clear session-revoked flag:", e); }
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate((u.role === "admin" || u.role === "sub_admin") ? "/admin" : (location.state?.from || "/wall"));
    } catch (e) {
      setErr(formatErr(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen zfd-canvas flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#FBBF24] font-bold mb-2">Zenith Flow Dynamics</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Operator Sign-In</h1>
          <p className="text-white/50 mt-2 text-sm">Wholesale Inventory Consignment Platform</p>
        </div>
        <div className="glass-card p-7 space-y-5">
          {kicked && (
            <div className="text-sm text-[#FCA5A5] bg-[#DC2626]/15 border border-[#DC2626]/40 rounded-md p-3" data-testid="login-kicked-notice">
              <strong>Session ended on this device.</strong> Your account was opened on another device.
              Only one active session is allowed at a time — sign in again to resume here.
            </div>
          )}
          <GoogleButton testid="login-google" label="Continue with Google" />
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold">
            <div className="flex-1 h-px bg-white/10" />
            or with email
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="zfd-label">Email Address</label>
              <input data-testid="login-email" className="zfd-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="zfd-label">Password</label>
              <input data-testid="login-password" className="zfd-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {err && <div data-testid="login-error" className="text-sm text-[#FCA5A5] bg-[#DC2626]/15 border border-[#DC2626]/40 rounded-md p-3">{err}</div>}
            <button data-testid="login-submit" disabled={loading} className="btn-deploy w-full disabled:opacity-50">
              {loading ? "Signing in…" : "Enter Operations Console"}
            </button>
          </form>
          <div className="text-center text-sm text-white/60 pt-2">
            No account? <Link to="/register" className="text-[#FBBF24] hover:underline font-semibold" data-testid="goto-register">Register Operator</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
